import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Wrench, ClipboardList, ChevronRight } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { colorFor } from "../../lib/userColors";
import PhaseChip from "../PhaseChip";

/**
 * Wave 8 — "My week" widget. Shows the next 7 days of:
 *  • job cards assigned to the current user (technician_id)
 *  • tasks the user is in `assigned_to_ids`
 * Grouped by day. Pure read-only — clicking any item deep-links to it.
 */
export default function MyWeekWidget() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [tasks, setTasks] = useState([]);

  useEffect(() => {
    if (!user) return;
    Promise.all([
      api.get("/jobcards"),
      api.get("/tasks"),
    ]).then(([j, t]) => {
      setJobs(j.data || []);
      setTasks(t.data || []);
    });
  }, [user]);

  const buckets = useMemo(() => {
    if (!user) return [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const horizon = new Date(today);
    horizon.setDate(horizon.getDate() + 7);

    // Build 7-day skeleton.
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(today);
      d.setDate(d.getDate() + i);
      days.push({
        date: d.toISOString().slice(0, 10),
        label: d.toLocaleDateString('en-GB', { weekday: "short", day: "numeric", month: "short" }),
        items: [],
      });
    }

    const myJobs = jobs.filter(
      (j) =>
        j.technician_id === user.id &&
        j.scheduled_at &&
        new Date(j.scheduled_at) >= today &&
        new Date(j.scheduled_at) < horizon &&
        !j.deleted_at,
    );
    const myTasks = tasks.filter(
      (t) =>
        Array.isArray(t.assigned_to_ids) &&
        t.assigned_to_ids.includes(user.id) &&
        (t.due_at || t.scheduled_at) &&
        new Date(t.due_at || t.scheduled_at) >= today &&
        new Date(t.due_at || t.scheduled_at) < horizon &&
        t.status !== "done" &&
        !t.deleted_at,
    );

    myJobs.forEach((j) => {
      const day = j.scheduled_at.slice(0, 10);
      const bucket = days.find((d) => d.date === day);
      if (bucket) {
        bucket.items.push({
          kind: "jobcard",
          id: j.id,
          title: j.title || `JC ${j.short_id || ""}`,
          time: j.scheduled_at,
          link: `/jobcards/${j.id}`,
          phase: j.phase,
        });
      }
    });
    myTasks.forEach((t) => {
      const at = t.due_at || t.scheduled_at;
      const day = at.slice(0, 10);
      const bucket = days.find((d) => d.date === day);
      if (bucket) {
        bucket.items.push({
          kind: "task",
          id: t.id,
          title: t.title,
          time: at,
          link: "/tasks",
        });
      }
    });

    return days;
  }, [jobs, tasks, user]);

  if (!user) return null;
  const userColor = user.color || colorFor(user.id);
  const totalCount = buckets.reduce((s, b) => s + b.items.length, 0);

  return (
    <section
      className={`hv-card p-4 flex flex-col overflow-hidden ${totalCount === 0 ? "md:h-[320px]" : "max-h-[420px] md:h-[320px] md:max-h-none"}`}
      data-testid="widget-my-week"
    >
      <header className="flex items-center justify-between gap-2 mb-3 shrink-0 pr-6">
        <div className="flex items-center gap-2 min-w-0">
          <div
            className="w-7 h-7 rounded-md flex items-center justify-center shrink-0"
            style={{ background: `${userColor}20`, border: `1px solid ${userColor}55`, color: userColor }}
          >
            <CalendarDays className="w-3.5 h-3.5" />
          </div>
          <h3 className="font-display text-sm font-bold tracking-tight truncate">My week</h3>
          <span
            className="text-[10px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded-full border shrink-0"
            style={{
              borderColor: `${userColor}55`,
              color: userColor,
              background: `${userColor}10`,
            }}
          >
            {totalCount}
          </span>
        </div>
        <Link
          to="/calendar"
          className="text-[10px] uppercase tracking-wider hover:underline shrink-0 inline-flex items-center gap-0.5"
          style={{ color: userColor }}
          data-testid="widget-my-week-open"
        >
          Calendar →
        </Link>
      </header>
      <div className="flex-1 min-h-0 overflow-y-auto -mx-1 px-1 hv-scroll">
        {totalCount === 0 ? (
          <div className="text-xs text-[#647087] italic py-6 text-center">
            Nothing on your plate this week. Enjoy.
          </div>
        ) : (
          buckets.map((b) =>
            b.items.length ? (
              <div key={b.date} className="mb-2.5" data-testid={`my-week-day-${b.date}`}>
                <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1 font-bold">
                  {b.label}
                </div>
                <ul className="space-y-1">
                  {b.items.map((it) => {
                    const Icon = it.kind === "jobcard" ? Wrench : ClipboardList;
                    return (
                      <li key={`${it.kind}-${it.id}`}>
                        <Link
                          to={it.link}
                          className="flex items-center gap-2 px-2 py-1.5 rounded border border-white/5 bg-white/[0.02] hover:border-[#1FB6C1]/40 transition text-[11px]"
                          data-testid={`my-week-item-${it.id}`}
                        >
                          <Icon
                            className="w-3 h-3 shrink-0"
                            style={{ color: userColor }}
                          />
                          <span className="truncate min-w-0 flex-1">{it.title}</span>
                          {it.kind === "jobcard" && it.phase && (
                            <PhaseChip
                              value={it.phase}
                              className="shrink-0 !text-[8px] !py-0 !px-1.5"
                            />
                          )}
                          <span className="font-mono text-[10px] text-[#647087] shrink-0">
                            {new Date(it.time).toLocaleTimeString('en-GB', {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                          <ChevronRight className="w-2.5 h-2.5 text-[#647087]" />
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null,
          )
        )}
      </div>
    </section>
  );
}
